import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import { fmtElapsed } from '../../lib/share/peer';
import { onRecordingChange } from '../../lib/rec/store';
import { TERMINAL_STATUSES, type RecordingView } from '../../lib/rec/types';
import { RecordingPlayer } from './RecordingPlayer';
import { fmtDate } from './RecordingCard';

/**
 * EL VISOR EMBEBIDO EN EL CENTRO (pedido del dueño 2026-09-10: "que le den click y lo vean embebido
 * en el medio de la pantalla"). Overlay fijo que monta el `RecordingPlayer` en el MEDIO, encima del
 * contenido y del visor de share (z 450) y del stack de banners (z 500), y DEBAJO de los modales
 * (z 4000). Se abre desde el banner de "nuevo video", desde la sección /recordings y desde la
 * `RecordingCard`. Lee `GET /:id` sola (como RecView) y marca `viewed` a través del player.
 *
 * No navega a `/rec/:id` ni a Messaging: la persona se queda donde estaba trabajando.
 */
export function RecordingViewerOverlay({ id, onClose }: { id: number; onClose: () => void }) {
  const { t, lang } = useI18n();
  const [data, setData] = useState<RecordingView | null>(null);
  const [err, setErr] = useState<'noAccess' | 'gone' | 'error' | null>(null);

  const load = useCallback(async (): Promise<RecordingView | null> => {
    try {
      const r = await api.get<RecordingView>(`/api/recordings/${id}`);
      setData(r);
      setErr(r.recording.status === 'deleted' ? 'gone' : null);
      return r;
    } catch (e) {
      const st = e instanceof ApiError ? e.status : 0;
      setErr(st === 403 ? 'noAccess' : st === 404 || st === 410 ? 'gone' : 'error');
      return null;
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => onRecordingChange(id, () => { void load(); }), [id, load]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Todavía procesándose: se relee cada 5 s SOLO con la pestaña visible (LEY escribir-al-leer: GET no escribe).
  const status = data?.recording.status;
  useEffect(() => {
    if (!status || TERMINAL_STATUSES.has(status)) return undefined;
    const iv = window.setInterval(() => { if (!document.hidden) void load(); }, 5_000);
    return () => clearInterval(iv);
  }, [load, status]);

  const refreshUrl = useCallback(async () => (await load())?.mediaUrl ?? null, [load]);
  const r = data?.recording ?? null;
  const ready = r?.status === 'ready' || r?.status === 'failed';
  const espera = r?.status === 'uploading' ? t('rec.statusUploading') : ready && !data?.mediaUrl ? t('rec.statusFailed') : t('rec.processing');

  return createPortal(
    <div className="rec-overlay" role="dialog" aria-modal="true" aria-label={r?.title ?? t('rec.previewTitle')}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rec-overlay-card">
        <div className="rec-overlay-head">
          <b className="rec-overlay-title" title={r?.title ?? undefined}>{r?.title ?? t('rec.previewTitle')}</b>
          {r && <span className="rec-overlay-meta">{r.owner.name} · {fmtDate(r.createdAt, lang)}{r.durationMs != null ? ` · ${fmtElapsed(r.durationMs)}` : ''}</span>}
          <span style={{ flex: 1 }} />
          <button type="button" className="btn ghost sm rec-overlay-x" onClick={onClose} aria-label={t('common.close')}><X size={16} strokeWidth={1.75} /></button>
        </div>
        <div className="rec-overlay-body">
          {err ? (
            <div className="rec-video rec-video-empty">{err === 'noAccess' ? t('rec.noAccess') : err === 'gone' ? t('rec.deleted') : t('rec.error')}</div>
          ) : ready && data?.mediaUrl ? (
            <RecordingPlayer id={id} mediaUrl={data.mediaUrl} posterUrl={data.posterUrl} title={r?.title} onExpired={refreshUrl} autoPlay />
          ) : (
            <div className="rec-overlay-wait">{data?.posterUrl && <img src={data.posterUrl} alt="" />}<span>{data ? espera : t('common.loading')}</span></div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
